import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./SearchResultsFilterSideBar.module.scss";
import AppGuidelineItem from "../AppSearchFilters/AppGuidelineItem";

export const normalizeSearchTerm = (term) =>
  term
    .split(/\s+/)
    .map((t) => t.trim().replace(/^\(+|\)+$/g, ""))
    .filter(Boolean)
    .filter((t) => t.toUpperCase() !== "AND")
    .filter((t, i, arr) => arr.indexOf(t) === i);

const EnhancedSearchFilterSideBar = ({
  search,
  navigate,
  setShowFilterBar,
  setFilterNumber,
  allGuidelines,
  setLastAppliedSearchTerm,
  searchWithin,
  setSearchWithin,
  setUpdateFilters,
}) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [selectedGuidelines, setSelectedGuidelines] = useState([]);

  useEffect(() => {
    setUpdateFilters(false);
    const searchParams = new URLSearchParams(search);
    const guidelines = searchParams.getAll("guidelinefilter");
    const searchTerm = searchParams.get("appendedinputbuttons");
    setLastAppliedSearchTerm(searchTerm);
    setSelectedGuidelines(guidelines);
    setFilterNumber(guidelines.length);
  }, [search, setFilterNumber]);

  const handleApplyFilter = () => {
    const normalizedWithin = normalizeSearchTerm(searchWithin);

    if (normalizedWithin.length) {
      setLastAppliedSearchTerm(normalizedWithin.join(" "));
    } else {
      const searchParams = new URLSearchParams(search);
      const originalSearch = searchParams.get("appendedinputbuttons") || "";
      setLastAppliedSearchTerm(originalSearch);
    }

    const searchParams = new URLSearchParams(search);
    const urlSearch = searchParams.get("appendedinputbuttons") || "";

    const newParams = new URLSearchParams();
    newParams.append("appendedinputbuttons", urlSearch);

    selectedGuidelines.forEach((guideline) => {
      newParams.append("guidelinefilter", guideline);
    });

    if (normalizedWithin.length) {
      newParams.append("searchWithin", normalizedWithin.join(" "));
    }

    setUpdateFilters(true);
    navigate(`/searchAction/?${newParams.toString()}`);
  };

  const handleClearAll = () => {
    setSelectedGuidelines([]);
    setFilterNumber(0);
    setSearchWithin("");
    setUpdateFilters(true);

    const searchParams = new URLSearchParams(search);
    const originalSearch = searchParams.get("appendedinputbuttons") || "";
    setLastAppliedSearchTerm(originalSearch);

    navigate(`/searchAction/?appendedinputbuttons=${originalSearch}`);
  };

  return (
    <div id="filter-container" className={styles.sidebar}>
      <div className={styles.headerRow}>
        <button
          onClick={() => setShowFilterBar(false)}
          className={styles.closeButton}
        >
          <i className="tg-icons-Cross"></i>
        </button>
        <span className={styles.title}>Filters</span>
        <button onClick={handleClearAll} className={styles.clearButton}>
          Clear all
        </button>
      </div>

      <div className={styles.section}>
        <span className={styles.subheading}>Filter by guideline</span>
        <ul className={styles.guidelineList}>
          {Object.keys(allGuidelines).length !== 0 &&
            Object.entries(allGuidelines)
              .sort(([, a], [, b]) => b - a)
              .map(([title, count]) => (
                <AppGuidelineItem
                  guideline={title}
                  resultCount={count || 0}
                  selectedGuideline={selectedGuidelines}
                  key={title}
                  updateSelected={setSelectedGuidelines}
                  data-testid="guideline-item"
                />
              ))}
        </ul>
      </div>

      <div className={classNames(styles.section, styles.searchWithin)}>
        <div className={styles.searchWithinTitle}>
          <span className={styles.subheading}>Search within results</span>
          <button
            className={styles.toggleButton}
            onClick={() => setIsSearchExpanded(!isSearchExpanded)}
            data-testid="search-within-result"
          >
            {isSearchExpanded ? (
              <i className="fa banner-head-topic fa-angle-down"></i>
            ) : (
              <i className="fa banner-head-topic fa-angle-up"></i>
            )}
          </button>
        </div>

        {isSearchExpanded && (
          <div className={styles.searchContent}>
            <label className={styles.searchLabel}>
              Type keywords to refine your current results:
            </label>
            <input
              type="text"
              className={styles.searchInput}
              value={searchWithin}
              onChange={(e) => setSearchWithin(e.target.value)}
              placeholder="e.g treatment, symptoms"
            />
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <button className={styles.applyButton} onClick={handleApplyFilter}>
          Apply filters
        </button>
      </div>
    </div>
  );
};

EnhancedSearchFilterSideBar.propTypes = {
  setShowFilterBar: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
  setFilterNumber: PropTypes.func.isRequired,
  allGuidelines: PropTypes.object.isRequired,
  setLastAppliedSearchTerm: PropTypes.func.isRequired,
  searchWithin: PropTypes.string.isRequired,
  setSearchWithin: PropTypes.func.isRequired,
};

export default EnhancedSearchFilterSideBar;
